import React from 'react';
import { CallLog, UserProfile } from '../types';
import { Phone, Video, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, Plus } from 'lucide-react';

interface CallsViewProps {
  currentUser: UserProfile;
  calls: CallLog[];
  onStartCall: (targetUser: { username: string; displayName: string; avatar: string }, type: 'voice' | 'video') => void;
}

export const CallsView: React.FC<CallsViewProps> = ({ currentUser, calls, onStartCall }) => {
  const formatDuration = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const s = sec % 60;
    return `${mins} دقيقة و ${s} ثانية`;
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#060911] font-['Cairo']">
      <div className="max-w-2xl mx-auto mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <span>سجل مكالمات وتين</span>
            <Phone className="w-5 h-5 text-emerald-400" />
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            مكالمات صوت وفيديو مشفرة طرفاً لطرف فائقة النقاء
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto divide-y divide-slate-800/60 bg-[#0e1422] border border-slate-800/80 rounded-3xl overflow-hidden shadow-xl">
        {calls.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Phone className="w-10 h-10 mx-auto mb-2 text-slate-600" />
            <p className="text-xs">لا يوجد سجل مكالمات حتى الآن</p>
          </div>
        ) : (
          calls.map((call) => (
            <div
              key={call.id}
              className="p-4 flex items-center justify-between hover:bg-[#121927] transition"
            >
              <div className="flex items-center gap-3">
                <img
                  src={call.targetUser.avatar}
                  alt={call.targetUser.displayName}
                  className="w-11 h-11 rounded-2xl object-cover ring-1 ring-slate-800"
                />
                <div>
                  <h4 className="text-xs font-bold text-white mb-0.5">
                    {call.targetUser.displayName}
                  </h4>
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    {call.direction === 'incoming' && (
                      <span className="flex items-center gap-1 text-emerald-400">
                        <PhoneIncoming className="w-3.5 h-3.5" />
                        <span>مستلمة</span>
                      </span>
                    )}
                    {call.direction === 'outgoing' && (
                      <span className="flex items-center gap-1 text-indigo-400">
                        <PhoneOutgoing className="w-3.5 h-3.5" />
                        <span>صادرة</span>
                      </span>
                    )}
                    {call.direction === 'missed' && (
                      <span className="flex items-center gap-1 text-rose-400 font-semibold">
                        <PhoneMissed className="w-3.5 h-3.5" />
                        <span>فائتة</span>
                      </span>
                    )}
                    <span>•</span>
                    <span>{formatDuration(call.durationSec)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => onStartCall(call.targetUser, 'voice')}
                  className="p-2.5 rounded-xl bg-[#141b27] hover:bg-emerald-950/40 border border-slate-800 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-400 transition"
                  title="إعادة الاتصال صوتياً"
                >
                  <Phone className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onStartCall(call.targetUser, 'video')}
                  className="p-2.5 rounded-xl bg-[#141b27] hover:bg-emerald-950/40 border border-slate-800 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-400 transition"
                  title="إعادة الاتصال بالفيديو"
                >
                  <Video className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
